/**
 * Constants and helper functions for data display
 */

/**
 * Provider icons for data source sections
 */
const PROVIDER_ICONS: Record<string, string> = {
  gv100: '🏛️',
  destatis: '🏛️',
  regionalstatistik: '📈',
  inkar: '📊',
  prognos: '🔮',
  mb_research: '💰',
  csv_import: '📋',
  default: '📊',
}

/**
 * Provider display names
 */
const PROVIDER_NAMES: Record<string, string> = {
  gv100: 'Gemeindeverzeichnis (GV100)',
  destatis: 'Statistisches Bundesamt',
  regionalstatistik: 'Regionalstatistik',
  inkar: 'INKAR (Raumbeobachtung)',
  prognos: 'Prognos',
  mb_research: 'MB-Research',
  csv_import: 'CSV Import',
}

/**
 * Get the icon for a data provider
 */
export function getProviderIcon(provider: string | undefined): string {
  if (!provider) return PROVIDER_ICONS.default
  return PROVIDER_ICONS[provider.toLowerCase()] || PROVIDER_ICONS.default
}

/**
 * Get the display name for a data provider
 */
export function getProviderName(provider: string | undefined): string {
  if (!provider) return 'Datenquelle'
  return PROVIDER_NAMES[provider.toLowerCase()] || provider
}

/**
 * Fields that represent integer values (no decimal places)
 */
const INTEGER_FIELDS = new Set([
  'population',
  'num_municipalities',
  'num_counties',
  'num_states',
  'year',
  'reference_year',
  'rank',
  'overall_rank',
  'dynamics_rank',
  'strength_rank',
  'demography_rank',
  'prosperity_rank',
  'labor_market_rank',
  'innovation_rank',
  // German key aliases (used in GV100 data display)
  'einwohner',
  'postleitzahl',
])

/**
 * Field units for display
 */
const FIELD_UNITS: Record<string, string> = {
  area_km2: 'km²',
  population_density: 'EW/km²',
  purchasing_power_per_capita: '€',
  purchasing_power_total: 'Mio. €',
  growth_rate: '%',
  unemployment_rate: '%',
  employment_rate: '%',
  // German key aliases (used in GV100 data display)
  fläche_km2: 'km²',
  bevölkerungsdichte: 'EW/km²',
}

/**
 * Check if a field should be displayed as an integer (no decimal places)
 */
export function isIntegerField(fieldName: string): boolean {
  // Check exact match
  if (INTEGER_FIELDS.has(fieldName)) return true

  // Check suffix patterns
  if (fieldName.endsWith('_rank')) return true
  if (fieldName.endsWith('_count')) return true
  if (fieldName.endsWith('_index')) return true
  if (fieldName.startsWith('num_')) return true

  return false
}

/**
 * Override display labels for field keys whose auto-generated label is wrong
 * (e.g. "fläche_km2" would become "Fläche Km2" but should be just "Fläche")
 */
const DISPLAY_LABEL_OVERRIDES: Record<string, string> = {
  fläche_km2: 'Fläche',
  bevölkerungsdichte: 'Bevölkerungsdichte',
  amtl_bezeichnung: 'Amtl. Bezeichnung',
}

/**
 * Get an override display label for a field, if one exists.
 * Returns undefined when no override is defined.
 */
export function getDisplayLabelOverride(fieldName: string): string | undefined {
  return DISPLAY_LABEL_OVERRIDES[fieldName]
}

/**
 * Get the unit for a field (if any)
 */
export function getFieldUnit(fieldName: string): string | undefined {
  // Check exact match
  if (FIELD_UNITS[fieldName]) return FIELD_UNITS[fieldName]

  // Check patterns
  if (fieldName.includes('rate') || fieldName.includes('quota') || fieldName.includes('share')) {
    return '%'
  }
  if (fieldName.includes('area') && fieldName.includes('km')) {
    return 'km²'
  }
  if (fieldName.includes('density')) {
    return 'Einw./km²'
  }

  return undefined
}
